import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Snackbar,
  InputAdornment,
  Chip,
  Tooltip,
  Card,
  CardContent,
  CardActions,
  useMediaQuery,
  useTheme,
  Divider,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GridViewIcon from '@mui/icons-material/GridView';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import MapIcon from '@mui/icons-material/Map';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import type { Customer, Job } from '../types';
import { apiRequest, api } from '../utils/api';
import { formatPhoneNumber, formatPhoneInput, getWhatsAppLink } from '../utils/phoneUtils';
import { formatDateForDisplay } from '../utils/dateUtils';
import AddressMapModal from '../components/AddressMapModal';
import { useNavigate } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';



export default function Customer() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openJobsDialog, setOpenJobsDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [openAddressModal, setOpenAddressModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerJobs, setCustomerJobs] = useState<Job[]>([]);

  const [formData, setFormData] = useState<Omit<Customer, 'id'>>({
    name: '',
    email: '',
    phone: '',
    address: '',
    status: 'active',
    createdAt: new Date().toLocaleDateString('en-US'),
  });
  const [phones, setPhones] = useState<string[]>(['']);
  const [companyName, setCompanyName] = useState('');
  const [contactPerson, setContactPerson] = useState('');

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const response = await apiRequest('/customers', { method: 'GET' });
      const data = (response.data || []) as any[];
      // Map backend data to frontend Customer type
      const mapped: Customer[] = data.map((c) => ({
        id: c.id,
        name: c.name || '',
        email: c.email || '',
        phone: c.phone || '',
        address: c.address || '',
        status: 'active' as 'active' | 'inactive', // Default to active since backend doesn't have status
        createdAt: formatDateForDisplay(c.created_at),
      }));
      setCustomers(mapped);
    } catch (error) {
      console.error('Error loading customers:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to load customers',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = () => {
    loadCustomers();
    setSnackbar({ open: true, message: 'Data refreshed', severity: 'success' });
  };

  const handleCreate = () => {
    setEditingCustomer(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      status: 'active',
      createdAt: new Date().toLocaleDateString('en-US'),
    });
    setPhones(['']);
    setCompanyName('');
    setContactPerson('');
    setOpenDialog(true);
  };

  const handleEdit = async (id: number) => {
    try {
      const response = await apiRequest(`/customers/${id}`, { method: 'GET' });
      const customerData = response.data as any;
      const customer = customers.find((c) => c.id === id);
      if (customer) {
        setEditingCustomer(customer);
        setFormData({
          name: customerData.name || '',
          email: customerData.email || '',
          phone: customerData.phone || '',
          address: customerData.address || '',
          status: 'active' as 'active' | 'inactive',
          createdAt: formatDateForDisplay(customerData.created_at),
        });
        const phoneStr = customerData.phone || '';
        const phoneParts = phoneStr ? phoneStr.split(',').map((p: string) => p.trim()).filter((p: string) => p) : [''];
        setPhones(phoneParts.length > 0 ? phoneParts : ['']);
        setCompanyName(customerData.company_name || '');
        setContactPerson(customerData.contact_person || '');
        setOpenDialog(true);
      }
    } catch (error) {
      console.error('Error loading customer details:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load customer details',
        severity: 'error',
      });
    }
  };

  const handleDelete = (id: number) => {
    setDeleteId(id);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (deleteId !== null) {
      try {
        await api.delete(`/customers/${deleteId}`);
        setCustomers(customers.filter((c) => c.id !== deleteId));
        setSnackbar({ open: true, message: 'Customer deleted successfully', severity: 'success' });
        setOpenDeleteDialog(false);
        setDeleteId(null);
      } catch (error) {
        console.error('Error deleting customer:', error);
        setSnackbar({
          open: true,
          message: error instanceof Error ? error.message : 'Failed to delete customer',
          severity: 'error',
        });
        setOpenDeleteDialog(false);
        setDeleteId(null);
      }
    }
  };

  const handleSave = async () => {
    try {
      // Determine the actual name and contact person
      // If company name exists: name = company name, contact_person = contact person field (or name if not provided)
      // If no company name: name = person's name, contact_person = same as name (default)
      const isCompany = companyName.trim() !== '';
      const finalName = isCompany ? companyName.trim() : formData.name;
      const finalContactPerson = isCompany 
        ? (contactPerson.trim() || formData.name || companyName.trim()) 
        : (contactPerson.trim() || formData.name);

      const joinedPhone = phones.map(p => p.trim()).filter(p => p).join(', ') || null;

      const customerData = {
        name: finalName,
        email: formData.email || null,
        phone: joinedPhone,
        address: formData.address || null,
        company_name: isCompany ? companyName.trim() : null,
        contact_person: finalContactPerson,
      };

      if (editingCustomer) {
        // Update existing customer
        await api.put(`/customers/${editingCustomer.id}`, customerData);
        setSnackbar({ open: true, message: 'Customer updated successfully', severity: 'success' });
      } else {
        // Create new customer
        await api.post('/customers', customerData);
        setSnackbar({ open: true, message: 'Customer created successfully', severity: 'success' });
      }
      loadCustomers(); // Reload customers from backend
      setOpenDialog(false);
      setEditingCustomer(null);
    } catch (error) {
      console.error('Error saving customer:', error);
      const errorMessage = error instanceof Error 
        ? error.message 
        : (error as any)?.response?.data?.message || 'Failed to save customer';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    }
  };

  const handleDownload = () => {
    // Simulate download - in real app, this would export CSV/Excel
    const csv = [
      ['ID', 'Name', 'Email', 'Phone', 'Address', 'Status', 'Created At'],
      ...customers.map((c) => [
        c.id,
        c.name,
        c.email,
        c.phone,
        c.address,
        c.status,
        c.createdAt,
      ]),
    ]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'customers.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    setSnackbar({ open: true, message: 'Data exported successfully', severity: 'success' });
  };

  const handleCustomerNameClick = async (customer: Customer) => {
    setSelectedCustomer(customer);
    setOpenJobsDialog(true);
    setLoadingJobs(true);
    try {
      const response = await apiRequest(`/jobs?customerId=${customer.id}`, { method: 'GET' });
      const result = response.data;
      const data = result?.data || result || [];
      const mapped: Job[] = Array.isArray(data) ? data.map((j: any) => ({
        id: j.id,
        job: j.job_number,
        customer: j.customer_name || '',
        customerAddress: j.customer_address || '',
        jobAddress: j.job_address,
        apartment: j.apartment || '',
        status: (j.status || 'Estimate') as Job['status'],
        quoteStatus: j.quote_status as Job['quoteStatus'],
        payStatus: j.pay_status as Job['payStatus'],
        appointmentDate: j.appointment_date
          ? new Date(j.appointment_date).toLocaleDateString('en-US')
          : '',
        startTime: j.start_time || '',
        endTime: j.end_time || '',
        dueDate: j.due_date
          ? new Date(j.due_date).toLocaleDateString('en-US')
          : '',
        userId: j.user_id ?? undefined,
        active: j.active !== undefined ? j.active : true,
      })) : [];
      setCustomerJobs(mapped);
    } catch (error) {
      console.error('Error loading customer jobs:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load jobs for this customer',
        severity: 'error',
      });
      setCustomerJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  };

  const handleJobClick = (jobId: number) => {
    setOpenJobsDialog(false);
    navigate(`/jobs/${jobId}`);
  };

  const filteredCustomers = customers.filter(
    (c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.phone.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.address.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const displayedCustomers = filteredCustomers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="overline" color="text.secondary">
          Customers
        </Typography>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Customers
        </Typography>
      </Box>

      <Paper sx={{ width: '100%', mb: 2 }}>
        {showSearch && (
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <TextField
              fullWidth
              placeholder="Search customers..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0); // Reset to first page when searching
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchTerm('');
                        setShowSearch(false);
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              autoFocus
            />
          </Box>
        )}
        <Toolbar
          sx={{
            pl: { sm: 2 },
            pr: { xs: 1, sm: 1 },
            justifyContent: 'space-between',
            minHeight: { xs: 56 },
          }}
        >
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {!isMobile && (
              <IconButton title="Grid View">
                <GridViewIcon />
              </IconButton>
            )}
            {!isMobile && (
              <IconButton title="Filter">
                <FilterListIcon />
              </IconButton>
            )}
            <IconButton title="Download" onClick={handleDownload}>
              <DownloadIcon />
            </IconButton>
            <IconButton
              title="Search"
              onClick={() => setShowSearch(!showSearch)}
              color={showSearch ? 'primary' : 'default'}
            >
              <SearchIcon />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <IconButton title="Refresh" onClick={handleRefresh}>
              <RefreshIcon />
            </IconButton>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
              sx={{ bgcolor: 'grey.900', '&:hover': { bgcolor: 'grey.800' }, px: isMobile ? 1.5 : 2 }}
              size={isMobile ? 'small' : 'medium'}
            >
              Create
            </Button>
          </Box>
        </Toolbar>

        {isMobile ? (
          /* Mobile card view */
          <Box sx={{ p: 1 }}>
            {loading ? (
              <Box sx={{ textAlign: 'center', p: 3 }}>Loading...</Box>
            ) : displayedCustomers.length === 0 ? (
              <Box sx={{ textAlign: 'center', p: 3, color: 'text.secondary' }}>No customers found</Box>
            ) : (
              displayedCustomers.map((customer, index) => (
                  <Card key={customer.id} sx={{ mb: 1.5 }} variant="outlined">
                    <CardContent sx={{ pb: 1, '&:last-child': { pb: 1 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                        <Link
                          component="button"
                          variant="subtitle1"
                          onClick={() => handleCustomerNameClick(customer)}
                          sx={{ fontWeight: 600, textDecoration: 'none', textAlign: 'left', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {customer.name}
                        </Link>
                        <Chip
                          label={customer.status}
                          color={customer.status === 'active' ? 'success' : 'default'}
                          size="small"
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">#{index + 1 + page * rowsPerPage}</Typography>
                      <Divider sx={{ my: 1 }} />
                      {customer.email && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <Box component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>Email:</Box>
                          {customer.email}
                        </Typography>
                      )}
                      {customer.phone && (
                        <Box sx={{ mb: 0.5 }}>
                          <Typography variant="body2" component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>Phone:</Typography>
                          {customer.phone.split(',').map((p, idx) => {
                            const trimmed = p.trim();
                            const link = getWhatsAppLink(trimmed);
                            return (
                              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', ml: idx > 0 ? 0 : 0 }}>
                                <Typography variant="body2">{formatPhoneNumber(trimmed)}</Typography>
                                {link && (
                                  <Tooltip title="Open WhatsApp">
                                    <IconButton
                                      size="small"
                                      onClick={() => window.open(link, '_blank')}
                                      sx={{ color: '#25D366', ml: 0.5, p: 0.25, '&:hover': { backgroundColor: 'rgba(37, 211, 102, 0.1)' } }}
                                    >
                                      <WhatsAppIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                      {customer.address && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <Box component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>Address:</Box>
                          {customer.address}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">{customer.createdAt}</Typography>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'flex-end', pt: 0, px: 1, pb: 1 }}>
                      <IconButton size="small" onClick={() => handleEdit(customer.id)} title="Edit">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(customer.id)} title="Delete">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </CardActions>
                  </Card>
              ))
            )}
          </Box>
        ) : (
          /* Desktop table view */
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">Loading...</TableCell>
                  </TableRow>
                ) : displayedCustomers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">No customers found</TableCell>
                  </TableRow>
                ) : (
                  displayedCustomers.map((customer, index) => (
                      <TableRow key={customer.id} hover>
                        <TableCell>{index + 1 + page * rowsPerPage}</TableCell>
                        <TableCell>
                          <Link
                            component="button"
                            variant="body2"
                            onClick={() => handleCustomerNameClick(customer)}
                            sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                          >
                            {customer.name}
                          </Link>
                        </TableCell>
                        <TableCell>{customer.email}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {customer.phone ? customer.phone.split(',').map((p, idx) => {
                              const trimmed = p.trim();
                              const link = getWhatsAppLink(trimmed);
                              return (
                                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="body2">{formatPhoneNumber(trimmed)}</Typography>
                                  {link && (
                                    <Tooltip title="Open WhatsApp">
                                      <IconButton
                                        size="small"
                                        onClick={() => window.open(link, '_blank')}
                                        sx={{ color: '#25D366', p: 0.25, '&:hover': { backgroundColor: 'rgba(37, 211, 102, 0.1)' } }}
                                      >
                                        <WhatsAppIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              );
                            }) : <Typography variant="body2">-</Typography>}
                          </Box>
                        </TableCell>
                        <TableCell>{customer.address}</TableCell>
                        <TableCell>
                          <Chip
                            label={customer.status}
                            color={customer.status === 'active' ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{customer.createdAt}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleEdit(customer.id)} title="Edit">
                            <EditIcon />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDelete(customer.id)} title="Delete">
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          component="div"
          count={filteredCustomers.length}
          rowsPerPageOptions={[5, 10, 25]}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingCustomer ? 'Edit Customer' : 'Create New Customer'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Company Name (Optional)"
              value={companyName}
              onChange={(e) => {
                setCompanyName(e.target.value);
                // If company name is cleared, reset contact person logic
                if (!e.target.value.trim()) {
                  setContactPerson('');
                }
              }}
              fullWidth
              placeholder="Leave empty if customer is an individual"
              helperText="Leave empty if customer is not a company"
            />
            <TextField
              label="Contact Person"
              value={contactPerson}
              onChange={(e) => setContactPerson(e.target.value)}
              fullWidth
              placeholder={companyName.trim() ? "Enter contact person name" : formData.name || "Same as name"}
              helperText={companyName.trim() ? "Contact person (different from company name)" : "Defaults to name if not specified"}
            />
            <TextField
              label={companyName.trim() ? "Contact Person Name" : "Customer Name"}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              helperText={companyName.trim() ? "Name of the contact person at the company" : "Customer name (person's name)"}
            />
            <TextField
              label="Email"
              type="text"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              fullWidth
              required
              placeholder="email1@example.com, email2@example.com"
              helperText="Multiple emails can be separated by comma"
            />
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Phone Numbers
              </Typography>
              {phones.map((phone, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TextField
                    label={`Phone ${index + 1}`}
                    value={phone}
                    onChange={(e) => {
                      const formatted = formatPhoneInput(e.target.value);
                      const updated = [...phones];
                      updated[index] = formatted;
                      setPhones(updated);
                    }}
                    fullWidth
                    placeholder="(555) 123-4567"
                    inputProps={{
                      maxLength: 14,
                    }}
                    size="small"
                  />
                  {phones.length > 1 && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setPhones(phones.filter((_, i) => i !== index))}
                      title="Remove phone"
                    >
                      <RemoveCircleOutlineIcon />
                    </IconButton>
                  )}
                </Box>
              ))}
              <Button
                size="small"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => setPhones([...phones, ''])}
                sx={{ mt: 0.5 }}
              >
                Add Phone
              </Button>
            </Box>
            <TextField
              label="Address"
              value={formData.address}
              onClick={() => setOpenAddressModal(true)}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="Click to select address on map..."
              multiline
              rows={2}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setOpenAddressModal(true)}
                      edge="end"
                      title="Open map to select address"
                    >
                      <MapIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              label="Status"
              value={formData.status}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  status: e.target.value as 'active' | 'inactive',
                })
              }
              fullWidth
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
            <TextField
              label="Created At"
              value={formData.createdAt}
              onChange={(e) =>
                setFormData({ ...formData, createdAt: e.target.value })
              }
              fullWidth
              placeholder="MM/DD/YYYY"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.name || !formData.email}
          >
            {editingCustomer ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Delete Customer</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this customer? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Address Map Modal - Using Google Maps with American standard address format */}
      <AddressMapModal
        open={openAddressModal}
        onClose={() => setOpenAddressModal(false)}
        onConfirm={(address) => {
          setFormData({ ...formData, address });
          setOpenAddressModal(false);
        }}
        initialAddress={formData.address}
      />

      {/* Jobs Dialog */}
      <Dialog
        open={openJobsDialog}
        onClose={() => setOpenJobsDialog(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Jobs for {selectedCustomer?.name}
          {isMobile && (
            <IconButton onClick={() => setOpenJobsDialog(false)} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent sx={{ p: isMobile ? 1 : 2 }}>
          {loadingJobs ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : customerJobs.length === 0 ? (
            <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
              No jobs found for this customer.
            </Typography>
          ) : isMobile ? (
            /* Mobile job cards */
            <Box>
              {customerJobs.map((job) => (
                <Card
                  key={job.id}
                  sx={{ mb: 1.5, cursor: 'pointer' }}
                  variant="outlined"
                  onClick={() => handleJobClick(job.id)}
                >
                  <CardContent sx={{ pb: 1, '&:last-child': { pb: 1 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {job.job}
                      </Typography>
                      <Chip
                        label={job.status}
                        size="small"
                        color={
                          job.status === 'Finish' ? 'success' :
                          job.status === 'Fabrication' ? 'info' :
                          job.status === 'Install' ? 'warning' :
                          'default'
                        }
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {job.jobAddress}{job.apartment && `, ${job.apartment}`}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={job.quoteStatus || 'No Quote'}
                        size="small"
                        color={
                          job.quoteStatus === 'Accept' ? 'success' :
                          job.quoteStatus === 'Sent' ? 'info' :
                          job.quoteStatus === 'Reject' ? 'error' :
                          'default'
                        }
                      />
                      <Chip
                        label={job.payStatus || 'Unpaid'}
                        size="small"
                        color={job.payStatus === 'Paid' ? 'success' : 'default'}
                      />
                    </Box>
                    {(job.appointmentDate || job.dueDate) && (
                      <Box sx={{ mt: 1 }}>
                        {job.appointmentDate && (
                          <Typography variant="caption" color="text.secondary" sx={{ mr: 2 }}>
                            Appt: {job.appointmentDate}
                          </Typography>
                        )}
                        {job.dueDate && (
                          <Typography variant="caption" color="text.secondary">
                            Due: {job.dueDate}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            /* Desktop job table */
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Job Number</TableCell>
                    <TableCell>Job Address</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Quote Status</TableCell>
                    <TableCell>Pay Status</TableCell>
                    <TableCell>Appointment Date</TableCell>
                    <TableCell>Due Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customerJobs.map((job) => (
                    <TableRow key={job.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleJobClick(job.id)}>
                      <TableCell>
                        <Link
                          component="button"
                          variant="body2"
                          onClick={(e) => { e.stopPropagation(); handleJobClick(job.id); }}
                          sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {job.job}
                        </Link>
                      </TableCell>
                      <TableCell>{job.jobAddress}{job.apartment && `, ${job.apartment}`}</TableCell>
                      <TableCell>
                        <Chip
                          label={job.status}
                          size="small"
                          color={
                            job.status === 'Finish' ? 'success' :
                            job.status === 'Fabrication' ? 'info' :
                            job.status === 'Install' ? 'warning' :
                            'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={job.quoteStatus || 'No Quote'}
                          size="small"
                          color={
                            job.quoteStatus === 'Accept' ? 'success' :
                            job.quoteStatus === 'Sent' ? 'info' :
                            job.quoteStatus === 'Reject' ? 'error' :
                            'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Chip label={job.payStatus || 'Unpaid'} size="small" color={job.payStatus === 'Paid' ? 'success' : 'default'} />
                      </TableCell>
                      <TableCell>{job.appointmentDate || '-'}</TableCell>
                      <TableCell>{job.dueDate || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        {!isMobile && (
          <DialogActions>
            <Button onClick={() => setOpenJobsDialog(false)}>Close</Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
